import { useEffect, useState } from 'react';

import { observer } from 'mobx-react';

import { useStores } from '../../stores/useStores';
import { useMediaQuery } from '../useMediaQuery';

import RiveFilePreloader from './RiveFilePreloader';

const RiveFilePreloaderGroup = observer(() => {
  const { riveFileStore } = useStores();
  const { allFilesToPreload } = riveFileStore;
  const [ready, setReady] = useState(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    // Defer Rive preloading until after LCP to avoid competing for bandwidth
    // with the hero image. PerformanceObserver fires on LCP; requestIdleCallback
    // is the fallback for when LCP has already fired or isn't supported.
    let idleCallbackId: number | undefined;

    if (typeof PerformanceObserver !== 'undefined') {
      const lcpObserver = new PerformanceObserver((list) => {
        if (list.getEntries().length > 0) {
          lcpObserver.disconnect();
          setReady(true);
        }
      });

      try {
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch {
        // If LCP observation isn't supported, fall back to idle callback
        if ('requestIdleCallback' in window) {
          idleCallbackId = requestIdleCallback(() => setReady(true));
        } else {
          requestAnimationFrame(() => setTimeout(() => setReady(true), 0));
        }
      }

      return () => {
        lcpObserver.disconnect();
        if (idleCallbackId !== undefined) cancelIdleCallback(idleCallbackId);
      };
    }

    // Fallback: defer to idle
    if ('requestIdleCallback' in window) {
      idleCallbackId = requestIdleCallback(() => setReady(true));
      return () => {
        if (idleCallbackId !== undefined) cancelIdleCallback(idleCallbackId);
      };
    }

    requestAnimationFrame(() => setTimeout(() => setReady(true), 0));
  }, []);

  if (prefersReducedMotion || !ready || allFilesToPreload.length === 0) {
    return null;
  }

  return (
    <div>
      {allFilesToPreload.map((url) => (
        <RiveFilePreloader key={url} url={url} />
      ))}
    </div>
  );
});

export default RiveFilePreloaderGroup;
